'use client';

import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Pagination as PaginationComponent, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/shared/components/ui/pagination';
type PaginatorProps = {
  total?: number;
  perPage?: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
  showPreviousNext: boolean;
  className?: string;
};
function Paginator(t0) {
  const $ = _c(27);
  const {
    total,
    currentPage,
    totalPages,
    onPageChange,
    showPreviousNext,
    className
  } = t0;
  let t1;
  if ($[0] !== currentPage || $[1] !== total || $[2] !== totalPages) {
    t1 = <div className="text-muted-foreground text-sm">Total: {total}, Page: {currentPage} / {totalPages}</div>;
    $[0] = currentPage;
    $[1] = total;
    $[2] = totalPages;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex-1" />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== currentPage || $[6] !== onPageChange || $[7] !== showPreviousNext || $[8] !== totalPages) {
    t3 = showPreviousNext && totalPages && currentPage - 1 >= 1 ? <PaginationItem className="cursor-pointer" key="prev"><PaginationPrevious onClick={() => {
        if (currentPage - 1 >= 1) {
          onPageChange(currentPage - 1);
        }
      }} className="cursor-pointer" /></PaginationItem> : null;
    $[5] = currentPage;
    $[6] = onPageChange;
    $[7] = showPreviousNext;
    $[8] = totalPages;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== currentPage || $[11] !== onPageChange || $[12] !== totalPages) {
    t4 = generatePaginationLinks(currentPage, totalPages, onPageChange);
    $[10] = currentPage;
    $[11] = onPageChange;
    $[12] = totalPages;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== currentPage || $[15] !== onPageChange || $[16] !== showPreviousNext || $[17] !== totalPages) {
    t5 = showPreviousNext && totalPages && currentPage + 1 <= totalPages ? <PaginationItem className="cursor-pointer" key="next"><PaginationNext onClick={() => {
        if (currentPage + 1 <= totalPages) {
          onPageChange(currentPage + 1);
        }
      }} /></PaginationItem> : null;
    $[14] = currentPage;
    $[15] = onPageChange;
    $[16] = showPreviousNext;
    $[17] = totalPages;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t1 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <PaginationContent className="flex w-full justify-center">{t1}{t2}{t3}{t4}{t5}</PaginationContent>;
    $[19] = t1;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== className || $[25] !== t6) {
    t7 = <PaginationComponent className={className}>{t6}</PaginationComponent>;
    $[24] = className;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
const generatePaginationLinks = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
  const pages: JSX.Element[] = [];
  if (totalPages <= 6) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(<PaginationItem className="cursor-pointer" key={`page-${i}`}>
          <PaginationLink onClick={() => i !== currentPage && onPageChange(i)} isActive={i == currentPage} className="cursor-pointer">
            {i}
          </PaginationLink>
        </PaginationItem>);
    }
  } else {
    for (let i = 1; i <= 2; i++) {
      pages.push(<PaginationItem key={`page-${i}`}>
          <PaginationLink onClick={() => i !== currentPage && onPageChange(i)} isActive={i === currentPage} className="cursor-pointer">
            {i}
          </PaginationLink>
        </PaginationItem>);
    }
    if (2 < currentPage && currentPage < totalPages - 1) {
      pages.push(<PaginationEllipsis key={'ellipsis-1'} />);
      pages.push(<PaginationItem key={'page-current'}>
          <PaginationLink onClick={() => onPageChange(currentPage)} isActive={true} className="cursor-pointer">
            {currentPage}
          </PaginationLink>
        </PaginationItem>);
    }
    pages.push(<PaginationEllipsis key={'ellipsis-2'} />);
    for (let i = totalPages - 1; i <= totalPages; i++) {
      pages.push(<PaginationItem key={`page-${i}`}>
          <PaginationLink onClick={() => i !== currentPage && onPageChange(i)} isActive={i === currentPage} className="cursor-pointer">
            {i}
          </PaginationLink>
        </PaginationItem>);
    }
  }
  return pages;
};
export function Pagination(t0) {
  const $ = _c(11);
  let {
    total,
    limit,
    page: t1,
    className
  } = t0;
  const page = t1 === undefined ? 1 : t1;
  if (limit <= 0) {
    limit = 30;
  }
  const totalPages = Math.ceil(total / limit);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  let t2;
  if ($[0] !== pathname || $[1] !== router || $[2] !== searchParams) {
    t2 = page_0 => {
      const params = new URLSearchParams(searchParams);
      params.set("page", page_0.toString());
      router.push(`${pathname}?${params.toString()}`);
    };
    $[0] = pathname;
    $[1] = router;
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handlePageChange = t2;
  if (totalPages <= 1) {
    return null;
  }
  const t3 = Number(total);
  const t4 = Number(limit);
  const t5 = Number(page);
  const t6 = Number(totalPages);
  let t7;
  if ($[4] !== className || $[5] !== handlePageChange || $[6] !== t3 || $[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = <Paginator total={t3} perPage={t4} currentPage={t5} totalPages={t6} onPageChange={handlePageChange} showPreviousNext={true} className={className} />;
    $[4] = className;
    $[5] = handlePageChange;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}